import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { File, Clock, CheckCircle2, XCircle, Download, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getCertificateRequestsByUser, type CertificateRequest } from "@/lib/collections/certificateRequests/user/getCertificateRequests";
import { storage } from '@/lib/appwrite';
import { toast } from "react-toastify";
import AddCertificateRequest from './addRequest';
import { getApprovalConfig, type ApprovalRole } from '../config/certificateApprovals';



type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';
type ApprovalStatus = 'pending' | 'approved' | 'rejected';

const CERTIFICATE_TYPES = [
    { value: 'standard', label: 'Certificado Estándar' },
    { value: 'with_salary', label: 'Certificado con Salario' },
    { value: 'job_functions', label: 'Certificado con Funciones' },
] as const;

const ITEMS_PER_PAGE = 6;

const STATUS_CONFIG: Record<ApprovalStatus, {
    label: string;
    variant: 'default' | 'secondary' | 'destructive';
    icon: any;
    className?: string;
}> = {
    pending: {
        label: 'Pendiente',
        variant: 'secondary',
        className: 'bg-yellow-600 dark:bg-yellow-800 text-white dark:text-white',
        icon: Clock,
    },
    approved: {
        label: 'Aprobado',
        variant: 'default',
        icon: CheckCircle2,
        className: 'bg-green-500 hover:bg-green-600 text-white dark:bg-green-600 dark:text-white',
    },
    rejected: {
        label: 'Rechazado',
        variant: 'destructive',
        icon: XCircle,
    },
};

// UTILITY FUNCTIONS

const getOverallStatus = (request: CertificateRequest): ApprovalStatus => {
    // Usar el campo status de la BD directamente
    if (request.status === 'rejected') {
        return 'rejected';
    }

    if (request.status === 'approved') {
        return 'approved';
    }

    return 'pending';
};

const getTypeLabel = (type: string) => {
    const found = CERTIFICATE_TYPES.find(t => t.value === type);
    return found?.label || type;
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// SUBCOMPONENTS

const StatusBadge = ({ status }: { status: ApprovalStatus }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;

    return (
        <Badge
            variant={config.variant}
            className={`flex items-center gap-1 w-fit ${config.className || ''}`}
        >
            <Icon className="w-3 h-3" />
            {config.label}
        </Badge>
    );
};

const ApprovalStep = ({
    status,
    label,
    showLine = true,
    stepNumber
}: {
    status: string;
    label: string;
    showLine?: boolean;
    stepNumber?: number;
}) => {
    const getStepColor = () => {
        if (status === 'approved') return 'bg-green-500';
        // if (status === 'pending') return 'bg-yellow-500';
        if (status === 'rejected') return 'bg-red-500';
        return 'bg-gray-300';
    };

    const getStepIcon = () => {
        if (status === 'approved') return <CheckCircle2 className="w-4 h-4 text-white" />;
        // if (status === 'pending') return <Clock  className="w-4 h-4 text-white" />;
        if (status === 'rejected') return <XCircle className="w-4 h-4 text-white" />;
        return <span className="text-xs text-gray-600">{stepNumber}</span>;

    };

    return (
        <>
            <div className="flex items-center gap-1.5 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStepColor()}`}>
                    {getStepIcon()}
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{label}</span>
            </div>
            {showLine && (
                <div className={`h-0.5 w-3 sm:w-4 flex-shrink-0  sm:mx-0 ${status === 'approved' ? 'bg-green-500' : 'bg-gray-300'}`} />
            )}
        </>
    );
};

const ApprovalSteps = ({
    request
}: {
    request: CertificateRequest;
}) => {
    const config = getApprovalConfig(request.type);
    const requiredApprovals = config.requiredApprovals;

    // Mapeo de roles a estados y labels
    const approvalData: Record<ApprovalRole, { status: string; label: string }> = {
        rrhh: { status: request.statusByRrhh, label: 'RRHH' },
        supervisor: { status: request.statusBySupervisor, label: 'Supervisor' },
        manager: { status: request.statusByManager, label: 'Gerencia' }
    };

    return (
        <div className="flex items-center gap-1.5 sm:gap-2 mt-3">
            {requiredApprovals.map((role, index) => {
                const data = approvalData[role];
                const isLast = index === requiredApprovals.length - 1;

                return (
                    <ApprovalStep
                        key={role}
                        status={data.status}
                        label={data.label}
                        showLine={!isLast}
                        stepNumber={index + 1}

                    />
                );
            })}
        </div>
    );
};

const RejectionNotice = ({ reason }: { reason: string }) => {
    return (
        <div className="border border-red-200 rounded-md p-3">
            <p className="text-xs font-medium text-red-800 dark:text-red-500 mb-1">Motivo del rechazo:</p>
            <p className="text-xs text-red-300 dark:text-gray-400">{reason}</p>
        </div>
    );
};

const EmptyState = ({ hasRequests }: { hasRequests: boolean }) => {
    return (
        <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
                <File className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                    {hasRequests ? 'No se encontraron solicitudes' : 'No tienes solicitudes'}
                </h3>
                <p className="text-muted-foreground text-center px-4">
                    {hasRequests
                        ? 'Intenta cambiar los filtros de búsqueda'
                        : 'Crea tu primera solicitud haciendo clic en "Nueva Solicitud"'
                    }
                </p>
            </CardContent>
        </Card>
    );
};

const RequestCard = ({
    request,
    onDownload
}: {
    request: CertificateRequest;
    onDownload: (fileId: string, certificateType: string) => void;
}) => {
    const overallStatus = getOverallStatus(request);
    const hasFile = !!request.certificateFileId;
    const rejectionReason = request.commentsRrhh || request.commentsManager;

    return (
        <Card className="hover:shadow-md transition-shadow overflow-hidden">
            <CardHeader className="overflow-hidden">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0 flex-1">
                        <CardTitle className="text-lg break-words overflow-wrap-anywhere">
                            {getTypeLabel(request.type)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span className="text-xs">{formatDate(request.requestDate)}</span>
                        </CardDescription>
                    </div>
                    <StatusBadge status={overallStatus} />
                </div>
            </CardHeader>
            <CardContent className="space-y-4 overflow-hidden">
                <p className="text-sm text-muted-foreground line-clamp-2 break-words overflow-wrap-anywhere">
                    {request.reason}
                </p>

                <ApprovalSteps request={request} />

                {overallStatus === 'rejected' && rejectionReason && (
                    <RejectionNotice reason={rejectionReason} />
                )}

                {request.status === 'approved' && hasFile && (
                    <Button
                        className="w-full gap-2 cursor-pointer"
                        variant="outline"
                        onClick={() => onDownload(request.certificateFileId, request.type)}
                    >
                        <Download className="w-4 h-4" />
                        Descargar Certificado
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

const Pagination = ({
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    total,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    total: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <p className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1}-{endIndex} de {total}
            </p>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center px-3 text-sm font-medium">
                    {currentPage} / {totalPages}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

const getBucketIdByType = (certificateType: string): string => {
    switch (certificateType) {
        case 'standard':
            return import.meta.env.VITE_BUCKET_ID_STANDAR_EMPLOYMENT_CERTIFICATES;
        case 'with_salary':
            return import.meta.env.VITE_BUCKET_ID_EMPLOYMENT_CERTIFICATE_WITH_SALARY;
        case 'job_functions':
            return import.meta.env.VITE_BUCKET_ID_EMPLOYMENT_CERTIFICATE_wITH_FUNCTIONS;
        default:
            return import.meta.env.VITE_BUCKET_ID_STANDAR_EMPLOYMENT_CERTIFICATES;
    }
};

// MAIN COMPONENT

const EmployeeRequestsView = () => {
    const { user } = useAuth();

    // State
    const [requests, setRequests] = useState<CertificateRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Load requests
    useEffect(() => {
        if (user?.$id) {
            loadRequests();
        }
    }, [user?.$id]);

    const loadRequests = async () => {
        if (!user?.$id) return;

        setLoading(true);
        const result = await getCertificateRequestsByUser(user.$id);

        if (result.success && result.data) {
            setRequests(result.data);
        } else {
            toast.error('Error al cargar las solicitudes');
        }
        setLoading(false);
    };

    const handleDownload = (fileId: string, certificateType: string) => {
        try {
            const BUCKET_ID = getBucketIdByType(certificateType);
            const fileUrl = storage.getFileDownload(BUCKET_ID, fileId);
            window.open(fileUrl.toString(), '_blank');
        } catch (error) {
            console.error('Error al descargar:', error);
            toast.error('Error al descargar el archivo');
        }
    };
    // Filtering and pagination
    const filteredRequests = requests.filter(request => {
        const overallStatus = getOverallStatus(request);
        const matchesStatus = statusFilter === 'all' || overallStatus === statusFilter;
        const matchesSearch =
            searchTerm === '' ||
            getTypeLabel(request.type).toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.reason.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesStatus && matchesSearch;
    });

    const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredRequests.length);
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    const handleFilterChange = () => {
        setCurrentPage(1);
    };


    if (loading) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                    <p className="text-muted-foreground">Cargando solicitudes...</p>
                </CardContent>
            </Card>
        );
    }


    return (

        <div className="py-6 pb-24">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Certificados</h2>
                    <p className="text-muted-foreground text-sm">Gestiona tus solicitudes</p>
                </div>
                <AddCertificateRequest onSuccess={loadRequests} />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Input
                        placeholder="Buscar por tipo o motivo..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleFilterChange();
                        }}
                        className="pl-9"
                    />
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>

                <Select
                    value={statusFilter}
                    onValueChange={(v) => {
                        setStatusFilter(v as StatusFilter);
                        handleFilterChange();
                    }}
                >
                    <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Filtrar por estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="approved">Aprobados</SelectItem>
                        <SelectItem value="rejected">Rechazados</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Content */}
            {paginatedRequests.length === 0 ? (
                <EmptyState hasRequests={requests.length > 0} />
            ) : (
                <>
                    <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                        {paginatedRequests.map((request) => (
                            <RequestCard
                                key={request.$id}
                                request={request}
                                onDownload={handleDownload}
                            />
                        ))}
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        total={filteredRequests.length}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </div>
    );
};

export default EmployeeRequestsView;